import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useEffect, useMemo, useState, useContext } from 'react';

import { useAuthenticator } from '@aws-amplify/ui-react';
import type { JWT, AuthSession } from 'aws-amplify/auth';
import { fetchAuthSession } from 'aws-amplify/auth';

type LoadSessionOptions = {
  forceRefresh?: boolean;
};

type ContextType = {
  /**
   * Cognito の ID トークン
   */
  idToken: JWT | undefined;
  /**
   * ID トークンの有効期限
   */
  expiration: Date | undefined;
  /**
   * サインイン処理の完了を示すフラグ
   */
  isConfigured: boolean;
  /**
   * サインイン判定を示すフラグ
   */
  isSignedIn: boolean;
  /**
   * 明示的にセッションをロードする関数
   */
  loadSession: (options?: LoadSessionOptions | undefined) => Promise<AuthSession | undefined>;
};

export const SessionContext = createContext<ContextType>({
  idToken: undefined,
  expiration: undefined,
  isConfigured: false,
  isSignedIn: false,
  loadSession: async () => undefined,
});

/**
 * セッション情報を提供するProviderです。
 * サインイン状態に応じてセッション情報(idToken)を取得します。
 * Note:
 *  - サインイン済みかどうかは idToken が存在するかどうかで判断してください。
 *    - idToken が存在する => ブラウザにidTokenが存在するが、idTokenが有効期限内であることは保証されない
 *  - idTokenの有効期限が切れた状態で loadSession() を呼び出すと最新のidTokenに更新されます。
 */
export const SessionProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const [session, setSession] = useState<AuthSession>();
  const [isConfigured, setIsConfigured] = useState(false);
  const idToken = useMemo(() => session?.tokens?.idToken, [session]);
  const expiration = useMemo(() => session?.credentials?.expiration, [session]);
  const isSignedIn = useMemo(() => authStatus === 'authenticated', [authStatus]);
  const loadSession = useCallback(
    async (options?: LoadSessionOptions) => {
      const session = await fetchAuthSession(options);
      setSession(session);
      return session;
    },
    [setSession]
  );

  useEffect(() => {
    switch (authStatus) {
      case 'configuring':
        if (isConfigured) setIsConfigured(false);
        break;
      case 'authenticated':
        loadSession();
        setIsConfigured(true);
        break;
      case 'unauthenticated':
        setIsConfigured(true);
        break;
      default:
        break;
    }
  }, [authStatus]);

  return (
    <SessionContext.Provider value={{ idToken, expiration, isConfigured, isSignedIn, loadSession }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext);
